import { optionalPositiveInteger, optionalTrimmedString } from '../../../lib/routeSearch';

import {
  dealPageSizes,
  DealSortDirection,
  DealStatus,
  defaultDealsFilter,
  isDealSortProperty,
  type DealSortProperty,
  type DealsFilter,
} from './model';

export interface DealsRouteSearch {
  search?: string;
  statuses?: string;
  page?: number;
  size?: number;
  sortProperty?: DealSortProperty | null;
  sortDirection?: DealSortDirection;
}

export function dealsRouteSearchSchema(search: Record<string, unknown>): DealsRouteSearch {
  const searchTerm = optionalTrimmedString(search['search']);
  const statuses = optionalTrimmedString(search['statuses']);
  const page = optionalPositiveInteger(search['page']);
  const parsedSize = optionalPositiveInteger(search['size']);
  const size = parsedSize && dealPageSizes.includes(parsedSize) ? parsedSize : undefined;
  const rawSortProperty = optionalTrimmedString(search['sortProperty']);
  const sortProperty =
    rawSortProperty && isDealSortProperty(rawSortProperty) ? rawSortProperty : undefined;
  const sortDirection = Object.values(DealSortDirection).includes(
    search['sortDirection'] as DealSortDirection,
  )
    ? (search['sortDirection'] as DealSortDirection)
    : undefined;

  return {
    ...(searchTerm ? { search: searchTerm } : {}),
    ...(statuses ? { statuses } : {}),
    ...(page ? { page } : {}),
    ...(size ? { size } : {}),
    ...(sortProperty ? { sortProperty } : {}),
    ...(search['sortProperty'] === null ? { sortProperty: null } : {}),
    ...(sortDirection ? { sortDirection } : {}),
  };
}

export function parseDealsRouteSearch(search: DealsRouteSearch): DealsFilter {
  return {
    searchTerm: search.search ?? defaultDealsFilter.searchTerm,
    statuses: parseStatuses(search.statuses) ?? defaultDealsFilter.statuses,
    page: search.page ? search.page - 1 : defaultDealsFilter.page,
    size: search.size ?? defaultDealsFilter.size,
    sortProperty:
      search.sortProperty === null
        ? null
        : (search.sortProperty ?? defaultDealsFilter.sortProperty),
    sortDirection: search.sortDirection ?? defaultDealsFilter.sortDirection,
  };
}

export function serializeDealsRouteSearch(filter: DealsFilter): DealsRouteSearch {
  return {
    ...(filter.searchTerm.trim() ? { search: filter.searchTerm.trim() } : {}),
    ...(sameStatuses(filter.statuses, defaultDealsFilter.statuses)
      ? {}
      : { statuses: filter.statuses.join(',') }),
    ...(filter.page ? { page: filter.page + 1 } : {}),
    ...(filter.size !== defaultDealsFilter.size ? { size: filter.size } : {}),
    ...(filter.sortProperty !== defaultDealsFilter.sortProperty
      ? { sortProperty: filter.sortProperty }
      : {}),
    ...(filter.sortDirection !== defaultDealsFilter.sortDirection
      ? { sortDirection: filter.sortDirection }
      : {}),
  };
}

const dealStatuses: ReadonlySet<string> = new Set(Object.values(DealStatus));

function parseStatuses(value: string | undefined) {
  if (value === undefined) return;
  return value.split(',').filter((status): status is DealStatus => dealStatuses.has(status));
}

function sameStatuses(left: DealStatus[], right: DealStatus[]) {
  const rightStatuses = new Set(right);
  return left.length === right.length && left.every((status) => rightStatuses.has(status));
}
